/**
 * Helper functions for counting travelers with relevant frequent flyer numbers.
 *
 * These functions are used to track the `frequent_flyer_added_count` property
 * in analytics events for air and package bookings.
 */

import { IPerson, TripDetails } from "redmond";

/**
 * Extract all unique airline codes from the booked flight segments.
 * Used to determine which frequent flyer programs are relevant to the booking.
 *
 * @param tripDetails - The trip details containing slices and segments
 * @returns Set of airline codes (e.g., Set(['DL', 'UA']))
 */
export const getBookedAirlineCodes = (tripDetails: TripDetails | null): Set<string> => {
  const airlineCodes: string[] = [];
  tripDetails?.slices?.forEach((slice) => {
    slice.segmentDetails?.forEach((segment) => {
      if (segment?.marketingAirline?.code) {
        airlineCodes.push(segment.marketingAirline.code);
      }
    });
  });
  return new Set(airlineCodes);
};

/**
 * Check if a passenger has at least one frequent flyer number for airlines in the booking.
 * Note: Returns true if ANY eligible FF number exists, ensuring each traveler is counted only once.
 *
 * @param passenger - The passenger/traveler to check
 * @param airlineCodes - Set of airline codes in the booking
 * @returns true if passenger has at least one relevant FF number
 */
export const hasRelevantFrequentFlyer = (
  passenger: IPerson,
  airlineCodes: Set<string>
): boolean =>
  Object.entries(passenger.frequentFlyer || {}).some(
    ([airlineCode, ff]) =>
      airlineCodes.has(airlineCode) && ff?.value?.trim()
  );

const PROGRAM_DISPLAY_NAME_TO_AIRLINE: Record<string, string> = {
  "AAdvantage for business": "AA",
  "Delta Skymiles for business": "DL",
};

/**
 * Filter business loyalty programs to only those matching airlines in the booking.
 *
 * @param businessLoyaltyPrograms - Display names from corporateInfo.businessLoyaltyPrograms
 * @param bookedAirlineCodes - Set of airline codes in the booking (from getBookedAirlineCodes)
 * @returns Array of program display names that match booked airlines
 */
export const getMatchedBusinessLoyaltyPrograms = (
  businessLoyaltyPrograms: string[] | undefined,
  bookedAirlineCodes: Set<string>
): string[] =>
  (businessLoyaltyPrograms ?? []).filter(
    (program) => bookedAirlineCodes.has(PROGRAM_DISPLAY_NAME_TO_AIRLINE[program])
  );
